import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** User-facing appearance preference. `system` follows the OS setting. */
export type ThemePref = 'light' | 'dark' | 'system';

/** The concrete theme actually applied to the DOM. */
export type ResolvedTheme = 'light' | 'dark';

type ThemeState = {
  pref: ThemePref;
  setPref: (pref: ThemePref) => void;
};

/**
 * Theme preference store. Persisted to localStorage under `pm-theme` so the
 * choice survives reloads.
 *
 * ⚠️ The persist key and shape (`{ state: { pref } }`) are mirrored by the
 * no-flash bootstrap script in `index.html`. If you rename the key or change
 * the shape, update that script too.
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      pref: 'system',
      setPref: (pref) => set({ pref }),
    }),
    { name: 'pm-theme' },
  ),
);

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Resolve a preference to a concrete theme. `system` consults the OS via
 * `prefers-color-scheme`. Mirror of the logic inlined in `index.html`.
 */
export function resolveTheme(pref: ThemePref): ResolvedTheme {
  if (pref === 'light' || pref === 'dark') return pref;
  return typeof window !== 'undefined' && window.matchMedia(DARK_QUERY).matches
    ? 'dark'
    : 'light';
}
